import * as Proto from '../Protos/protos';

const deserializeBoolean = (value: string) => value === 'true';
const deserializeEPGTypes = (value: string) => JSON.parse(value);
const deserializeString = (value: string) => value;
const deserializeNumber = (value: string) => Number.parseFloat(value);

const serializeBoolean = (value: boolean) => (value ? 'true' : 'false');
const serializeEPGTypes = (value: Proto.mediaarchiver.EPGType[]) => JSON.stringify(value);
const serializeString = (value: string) => value;
const serializeNumber = (value: number) => value.toString();

export interface IKeyValueStorage {
    analyticsAllowed: boolean;
    analyticsAsked: boolean;
    audienceMode: string;
    audienceWithVOSDAL: boolean;
    bug20220524: boolean;
    calendarDuration: number;
    consentGiven: boolean;
    expiringToken: string;
    expiringTokenExpires: number;
    incidentFirstTimeDisplayed: boolean;
    lastAnnotationType: string;
    lastBugReportedMail: string;
    lastBugReportedName: string;
    lastBugReportedPhone: string;
    lastBugReportedSociety: string;
    lastContentTypesRadio: Proto.mediaarchiver.EPGType[];
    lastContentTypesTV: Proto.mediaarchiver.EPGType[];
    lastMediaRadio: number;
    lastMediaRadioName: string;
    lastMediaTV: number;
    lastMediaTVName: string;
    lastMediaType: number;
    lastTalkProgramType: number;
    lastTalkStudy: number;
    lastTalkStudyType: number;
    lastTimelineDate: number;
    locale: string;
    stepBackward: number;
    stepFastBackward: number;
    stepFastForward: number;
    stepForward: number;
    storageBugSeen: boolean;
    volume: number;
    volumeBeforeMute: number;
}

const keyValueStorageDeserializers: { [key in KeyValueStorageKey]: (value: string) => IKeyValueStorage[key] } = {
    analyticsAllowed: deserializeBoolean,
    analyticsAsked: deserializeBoolean,
    audienceMode: deserializeString,
    audienceWithVOSDAL: deserializeBoolean,
    bug20220524: deserializeBoolean,
    calendarDuration: deserializeNumber,
    consentGiven: deserializeBoolean,
    expiringToken: deserializeString,
    expiringTokenExpires: deserializeNumber,
    incidentFirstTimeDisplayed: deserializeBoolean,
    lastAnnotationType: deserializeString,
    lastBugReportedMail: deserializeString,
    lastBugReportedName: deserializeString,
    lastBugReportedPhone: deserializeString,
    lastBugReportedSociety: deserializeString,
    lastContentTypesRadio: deserializeEPGTypes,
    lastContentTypesTV: deserializeEPGTypes,
    lastMediaRadio: deserializeNumber,
    lastMediaRadioName: deserializeString,
    lastMediaTV: deserializeNumber,
    lastMediaTVName: deserializeString,
    lastMediaType: deserializeNumber,
    lastTalkProgramType: deserializeNumber,
    lastTalkStudy: deserializeNumber,
    lastTalkStudyType: deserializeNumber,
    lastTimelineDate: deserializeNumber,
    locale: deserializeString,
    stepBackward: deserializeNumber,
    stepFastBackward: deserializeNumber,
    stepFastForward: deserializeNumber,
    stepForward: deserializeNumber,
    storageBugSeen: deserializeBoolean,
    volume: deserializeNumber,
    volumeBeforeMute: deserializeNumber,
};

const keyValueStorageSerializers: { [key in KeyValueStorageKey]: (value: IKeyValueStorage[key]) => string } = {
    analyticsAllowed: serializeBoolean,
    analyticsAsked: serializeBoolean,
    audienceMode: serializeString,
    audienceWithVOSDAL: serializeBoolean,
    bug20220524: serializeBoolean,
    calendarDuration: serializeNumber,
    consentGiven: serializeBoolean,
    expiringToken: serializeString,
    expiringTokenExpires: serializeNumber,
    incidentFirstTimeDisplayed: serializeBoolean,
    lastAnnotationType: serializeString,
    lastBugReportedMail: serializeString,
    lastBugReportedName: serializeString,
    lastBugReportedPhone: serializeString,
    lastBugReportedSociety: serializeString,
    lastContentTypesRadio: serializeEPGTypes,
    lastContentTypesTV: serializeEPGTypes,
    lastMediaRadio: serializeNumber,
    lastMediaRadioName: serializeString,
    lastMediaTV: serializeNumber,
    lastMediaTVName: serializeString,
    lastMediaType: serializeNumber,
    lastTalkProgramType: serializeNumber,
    lastTalkStudy: serializeNumber,
    lastTalkStudyType: serializeNumber,
    lastTimelineDate: serializeNumber,
    locale: serializeString,
    stepBackward: serializeNumber,
    stepFastBackward: serializeNumber,
    stepFastForward: serializeNumber,
    stepForward: serializeNumber,
    storageBugSeen: serializeBoolean,
    volume: serializeNumber,
    volumeBeforeMute: serializeNumber,
};

export type KeyValueStorageKey = keyof IKeyValueStorage;

export interface IUserStorage {
    del<T extends KeyValueStorageKey>(key: T): void;
    get<T extends KeyValueStorageKey>(key: T, defaultValue: IKeyValueStorage[T]): IKeyValueStorage[T];
    reset(): void;
    set<T extends KeyValueStorageKey>(key: T, value: IKeyValueStorage[T]): void;
}

export const TypedStorage: IUserStorage = {
    del: (key) => {
        localStorage.removeItem(key);
    },
    get: (key, defaultValue) => {
        const serialized = window.localStorage.getItem(key);

        if (serialized !== null) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return keyValueStorageDeserializers[key](serialized) as any;
        }
        return defaultValue;
    },
    reset: () => {
        localStorage.clear();
    },
    set: (key, value) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const serializer = keyValueStorageSerializers[key] as (t: any) => string;
        localStorage.setItem(key, serializer(value));
    },
};
